import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import clx from 'classnames';
import dayjs from 'dayjs';
import { MessageCircle } from 'lucide-react';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import useWindowSize from '../../../../utils/useWindowSize';
import { Api } from '../../../../entries/ApiTransport';
import { sendMessageToApp } from '../../../../utils/exsternalCall';
import { toastActions } from '../../../Toasts/store';
import { ROUTES } from '../../../../routes/constants';
import styles from './OtpCredits.module.scss';

const api = Api.getInstance();

const formatMoney = (amount, currency, language, fractionDigits = 2) =>
  new Intl.NumberFormat(language || 'it', {
    style: 'currency',
    currency: (currency || 'eur').toUpperCase(),
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);

const errorCode = (error) => error?.response?.data?.stringCode || '';

const COMPARE_AT_EUR = {
  300: 18.3,
  1000: 61,
};

const PER_CREDIT_EUR = {
  300: 0.05,
  1000: 0.04,
};

const isOtpVerificationOn = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

const formatAmount = (amount, language, fractionDigits = 2) =>
  new Intl.NumberFormat(language || 'it', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);

const OtpCredits = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useWindowSize();
  const refreshTimer = useRef(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saldo, setSaldo] = useState(null);
  const [prodotti, setProdotti] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [savingFlag, setSavingFlag] = useState(false);
  const [buyingPriceId, setBuyingPriceId] = useState('');
  const [needsBilling, setNeedsBilling] = useState(false);
  const handledCheckout = useRef('');
  const purchaseStarted = useRef(false);

  const notify = useCallback(
    (title, message, warning = false) => {
      dispatch(
        toastActions.OPEN_TOAST.SUCCESS({
          type: 'api',
          props: { title, message, warning },
        }),
      );
    },
    [dispatch],
  );

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const [saldoRes, prodottiRes, movimentiRes] = await Promise.all([
        api.get('/business/otp/saldo'),
        api.get('/business/otp/prodotti'),
        api.get('/business/otp/movimenti'),
      ]);
      setSaldo(saldoRes.data);
      setProdotti(
        [...(prodottiRes.data?.prodotti || [])].sort(
          (a, b) => Number(a.nCrediti) - Number(b.nCrediti),
        ),
      );
      setMovimenti(movimentiRes.data?.movimenti || []);
    } catch (error) {
      if (!silent) setLoadError(errorCode(error) || 'load');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    return () => window.clearTimeout(refreshTimer.current);
  }, [load]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible' || !purchaseStarted.current) return;
      load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [load]);

  useEffect(() => {
    const success = searchParams.get('success');
    if (success !== 'true' && success !== 'false') return;
    if (handledCheckout.current === success) return;
    handledCheckout.current = success;

    const next = new URLSearchParams(searchParams);
    next.delete('success');
    setSearchParams(next, { replace: true });

    if (success === 'false') {
      notify(
        t('settings.otp.checkout.cancelTitle'),
        t('settings.otp.checkout.cancelMessage'),
        true,
      );
      return;
    }

    notify(t('settings.otp.checkout.successTitle'), t('settings.otp.checkout.successMessage'));
    load({ silent: true });
    refreshTimer.current = window.setTimeout(() => load({ silent: true }), 2500);
  }, [searchParams, setSearchParams, load, notify, t]);

  const openCheckoutUrl = (url) => {
    if (!url) return;
    const fromApp = localStorage.getItem('fromApp') == 'true';
    if (fromApp) {
      sendMessageToApp('app:url', { url });
      return;
    }
    window.location.assign(url);
  };

  const handleToggle = async () => {
    if (!saldo || savingFlag) return;
    const nextActive = !isOtpVerificationOn(saldo.otpPrenotazioneAttivo);
    setSavingFlag(true);
    setSaldo((current) => ({ ...current, otpPrenotazioneAttivo: nextActive }));
    try {
      const { data } = await api.post('/business/otp/attiva', { attivo: nextActive });
      setSaldo((current) => ({
        ...current,
        otpPrenotazioneAttivo:
          data?.otpPrenotazioneAttivo == null
            ? nextActive
            : isOtpVerificationOn(data.otpPrenotazioneAttivo),
      }));
    } catch {
      setSaldo((current) => ({ ...current, otpPrenotazioneAttivo: !nextActive }));
      notify(t('settings.otp.toggle.errorTitle'), t('settings.otp.toggle.error'), true);
    } finally {
      setSavingFlag(false);
    }
  };

  const checkoutErrorMessage = (code) => {
    if (code === 'SALONE_SENZA_STRIPE') return t('settings.otp.packs.noStripe');
    if (code === 'OTP_PREZZO_NON_VALIDO') return t('settings.otp.packs.invalidPrice');
    if (code === 'OTP_CHECKOUT_NON_CONFIGURATO') return t('settings.otp.packs.checkoutUnavailable');
    return t('settings.otp.packs.genericError');
  };

  const handleBuy = async (priceId) => {
    if (!isOtpVerificationOn(saldo?.otpPrenotazioneAttivo) || !priceId || buyingPriceId) return;
    setBuyingPriceId(priceId);
    try {
      const { data } = await api.post('/business/otp/checkout', { priceId });
      if (!data?.url) {
        notify(t('settings.otp.checkout.errorTitle'), t('settings.otp.packs.genericError'), true);
        setBuyingPriceId('');
        return;
      }
      purchaseStarted.current = true;
      openCheckoutUrl(data.url);
      if (localStorage.getItem('fromApp') == 'true') setBuyingPriceId('');
    } catch (error) {
      const code = errorCode(error);
      if (code === 'SALONE_SENZA_STRIPE') setNeedsBilling(true);
      notify(t('settings.otp.checkout.errorTitle'), checkoutErrorMessage(code), true);
      setBuyingPriceId('');
    }
  };

  const movementLabel = (tipo) => {
    if (tipo === 'acquisto' || tipo === 'invio' || tipo === 'rimborso') {
      return t(`settings.otp.movements.${tipo}`);
    }
    return t('settings.otp.movements.other');
  };

  const crediti = Number(saldo?.creditiOtp) || 0;
  const attivo = isOtpVerificationOn(saldo?.otpPrenotazioneAttivo);
  const featuredPriceId = (() => {
    const rated = prodotti
      .map((pack) => {
        const credits = Number(pack.nCrediti) || 0;
        const amount = Number(pack.amount) || 0;
        if (credits <= 0 || amount <= 0) return null;
        return { priceId: pack.priceId, credits, perCredit: amount / credits };
      })
      .filter(Boolean);
    if (rated.length < 2) return null;
    const best = rated.reduce((winner, pack) => {
      if (pack.perCredit < winner.perCredit - 0.0001) return pack;
      if (Math.abs(pack.perCredit - winner.perCredit) < 0.0001 && pack.credits > winner.credits) {
        return pack;
      }
      return winner;
    });
    const isCheaper = rated.some(
      (pack) => pack.priceId !== best.priceId && pack.perCredit > best.perCredit + 0.0001,
    );
    return isCheaper ? best.priceId : null;
  })();

  return (
    <div className={clx(styles.page, { [styles.page_mobile]: isMobile })}>
      <div className={styles.header}>
        <TitleSectionBack
          title={t('settings.otp.title')}
          description={t('settings.otp.description')}
        />
      </div>

      {loading ? (
        <p className={styles.status} role="status">
          {t('settings.otp.loading')}
        </p>
      ) : null}

      {!loading && loadError ? (
        <div className={styles.error_card} role="alert">
          <p>{t('settings.otp.loadError')}</p>
          <button type="button" className={styles.secondary_button} onClick={() => load()}>
            {t('settings.otp.retry')}
          </button>
        </div>
      ) : null}

      {!loading && saldo ? (
        <>
          <section className={styles.card}>
            <div className={styles.balance}>
              <span className={styles.balance_icon} aria-hidden="true">
                <MessageCircle size={20} strokeWidth={2.2} />
              </span>
              <div>
                <p className={styles.balance_label}>{t('settings.otp.balance.title')}</p>
                <p className={styles.balance_value}>{crediti}</p>
              </div>
            </div>
            <p className={styles.hint}>{t('settings.otp.balance.refund')}</p>
            {attivo && crediti === 0 ? (
              <p className={styles.warning} role="status">
                {t('settings.otp.toggle.noCredits')}
              </p>
            ) : null}
          </section>

          <section className={styles.card}>
            <button
              type="button"
              className={clx(styles.toggle_row, { [styles.toggle_row_on]: attivo })}
              onClick={handleToggle}
              disabled={savingFlag}
              aria-pressed={attivo}
            >
              <span className={styles.toggle_copy}>
                <span className={styles.toggle_title}>{t('settings.otp.toggle.title')}</span>
                <span className={styles.toggle_hint}>{t('settings.otp.toggle.hint')}</span>
              </span>
              <span className={styles.toggle_switch} aria-hidden="true" />
            </button>
          </section>

          <section className={styles.section}>
            <div className={styles.section_head}>
              <h2 className={styles.section_title}>{t('settings.otp.packs.title')}</h2>
              <p className={styles.hint}>{t('settings.otp.packs.subtitle')}</p>
              {!attivo ? (
                <p className={styles.hint} role="status">
                  {t('settings.otp.packs.verificationOff')}
                </p>
              ) : null}
            </div>
            {prodotti.length === 0 ? (
              <p className={styles.hint}>{t('settings.otp.packs.empty')}</p>
            ) : (
              <div className={styles.packs}>
                {prodotti.map((pack, index) => {
                  const credits = Number(pack.nCrediti) || 0;
                  const amount = Number(pack.amount) || 0;
                  const perCredit = credits > 0 ? amount / 100 / credits : null;
                  const buying = buyingPriceId === pack.priceId;
                  const featured = pack.priceId === featuredPriceId;
                  const compareAt = COMPARE_AT_EUR[credits];
                  const shownPerCredit = PER_CREDIT_EUR[credits] ?? perCredit;
                  const toneClass = [styles.pack_tone_teal, styles.pack_tone_violet, styles.pack_tone_amber][
                    index % 3
                  ];
                  return (
                    <article
                      key={pack.priceId}
                      className={clx(styles.pack, toneClass, { [styles.pack_featured]: featured })}
                    >
                      <p className={styles.pack_count}>
                        {credits.toLocaleString(i18n.language)}
                      </p>
                      <div className={styles.pack_credits_row}>
                        <p className={styles.pack_credits}>
                          {t('settings.otp.packs.creditsLabel')}
                        </p>
                        {featured ? (
                          <span className={styles.pack_badge}>{t('settings.otp.packs.bestValue')}</span>
                        ) : null}
                      </div>
                      <p className={styles.pack_price}>
                        {compareAt != null ? (
                          <s className={styles.pack_price_was}>
                            {formatAmount(
                              compareAt,
                              i18n.language,
                              Number.isInteger(compareAt) ? 0 : 2,
                            )}
                          </s>
                        ) : null}
                        <span className={styles.pack_price_now}>
                          {formatMoney(amount / 100, pack.currency, i18n.language)}
                        </span>
                      </p>
                      {shownPerCredit != null ? (
                        <p className={styles.pack_unit}>
                          {t('settings.otp.packs.perCredit', {
                            price: formatMoney(shownPerCredit, pack.currency, i18n.language),
                          })}
                        </p>
                      ) : (
                        <span className={styles.pack_unit_spacer} />
                      )}
                      <button
                        type="button"
                        className={clx(styles.buy_button, {
                          [styles.buy_button_pending]: buying,
                          [styles.buy_button_locked]: !attivo,
                        })}
                        onClick={() => {
                          if (!attivo) return;
                          handleBuy(pack.priceId);
                        }}
                        disabled={!attivo || !pack.priceId || credits <= 0 || Boolean(buyingPriceId)}
                        aria-disabled={!attivo}
                      >
                        {buying ? t('settings.otp.packs.buying') : t('settings.otp.packs.buy')}
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
            {needsBilling ? (
              <button
                type="button"
                className={styles.link_button}
                onClick={() => navigate(`${ROUTES.SETTINGS}${ROUTES.PAYMENTS}`)}
              >
                {t('settings.otp.packs.goBilling')}
              </button>
            ) : null}
          </section>

          <section className={styles.card}>
            <h2 className={styles.section_title}>{t('settings.otp.movements.title')}</h2>
            {movimenti.length === 0 ? (
              <p className={styles.hint}>{t('settings.otp.movements.empty')}</p>
            ) : (
              <ul className={styles.movements}>
                {movimenti.map((row) => {
                  const delta = Number(row.delta) || 0;
                  const when = dayjs(row.createdAt);
                  return (
                    <li key={row.id} className={styles.movement}>
                      <span className={styles.movement_copy}>
                        <span className={styles.movement_title}>{movementLabel(row.tipo)}</span>
                        <span className={styles.hint}>
                          {when.isValid() ? when.format('DD/MM/YYYY HH:mm') : row.createdAt}
                        </span>
                      </span>
                      <span
                        className={clx(styles.movement_delta, {
                          [styles.movement_delta_plus]: delta > 0,
                          [styles.movement_delta_minus]: delta < 0,
                        })}
                      >
                        {delta > 0 ? `+${delta}` : delta}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default OtpCredits;
